import React, { useState, useEffect, useContext, useRef } from 'react';
import api from '../api/axiosConfig'; // Path relatif dari 'pages' ke 'api'
import AuthContext from '../context/AuthContext'; // Path relatif dari 'pages' ke 'context'
import ChangePasswordModal from '../components/ChangePasswordModal'
import { showSuccessToast, showErrorAlert } from '../utils/alert'; // Path relatif dari 'pages' ke 'utils'
import { FaUpload } from 'react-icons/fa';

// Impor CSS dari folder yang sesuai
import './ServicePage.css'; // File CSS bersama di folder 'pages'
import '../components/ImageUploader.css'; // File CSS uploader di folder 'components'
import '../components/DatePickerCustom.css'; // File CSS datepicker di folder 'components'

const EditProfilePage = () => {
  const { user, updateUserContext } = useContext(AuthContext); // Ambil user dan fungsi update context
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState(''); // Read-only
  const [role, setRole] = useState(''); // Read-only
  const [avatarFile, setAvatarFile] = useState(null); // File baru yang akan diupload
  const [preview, setPreview] = useState(null); // URL preview untuk file baru
  const [existingAvatarUrl, setExistingAvatarUrl] = useState(''); // URL avatar saat ini atau default
  const [loading, setLoading] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  
  const fileInputRef = useRef(null); // Referensi untuk input file

  // Isi form dengan data user saat ini dari context
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setRole(user.role || '');
      // Gunakan avatar dari context atau generate default Pravatar jika tidak ada
      setExistingAvatarUrl(user.avatar ? `${process.env.REACT_APP_API_URL}/uploads/${user.avatar}` : `https://i.pravatar.cc/150?u=${user.email || 'default'}`); // Pravatar sebagai default
      setPreview(null);
      setAvatarFile(null);
    }
  }, [user]); // Jalankan ulang jika user context berubah

  // Handler saat file dipilih
  const handleFileChange = (file) => {
    if (file && file.type.startsWith('image/')) {
      setAvatarFile(file);
      if (preview) URL.revokeObjectURL(preview); // Hapus preview lama
      setPreview(URL.createObjectURL(file)); // Buat preview baru
    }
  };

  // Handler saat tombol hapus preview ('x') diklik
  const handleRemovePreview = (e) => {
    e.stopPropagation(); // Mencegah trigger klik pada area di bawahnya
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setAvatarFile(null);
    // Kembali tampilkan avatar lama atau default jika preview dibatalkan
    setExistingAvatarUrl(user.avatar ? `${process.env.REACT_APP_API_URL}/uploads/${user.avatar}` : `https://i.pravatar.cc/150?u=${user.email || 'default'}`);
  };

  // Handler saat form disubmit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();
    formData.append('name', name);
    if (avatarFile) {
      formData.append('avatar', avatarFile);
    }

    try {
      const { data } = await api.put('/api/users/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Update context dengan data user baru (termasuk token baru agar data ter-refresh)
      updateUserContext(data); 
      showSuccessToast('Profil berhasil diperbarui!');
      // Reset state file setelah berhasil update untuk mencegah pengiriman ulang
      setAvatarFile(null);
      setPreview(null);
      // Update existingAvatarUrl dengan data baru (jika ada)
      setExistingAvatarUrl(data.avatar ? `${process.env.REACT_APP_API_URL}/uploads/${data.avatar}` : `https://i.pravatar.cc/150?u=${data.email || 'default'}`);

    } catch (error) {
      console.error("Gagal update profil:", error);
      showErrorAlert(error.response?.data?.message || "Gagal memperbarui profil.");
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk memicu klik pada input file tersembunyi
  const triggerFileSelect = () => {
      if (fileInputRef.current) {
          fileInputRef.current.click();
      }
  };

  // Tentukan gambar mana yang akan ditampilkan (preview baru atau gambar lama/default)
  const displayImage = preview || existingAvatarUrl;

  return (
    <>
      <div className="page-container">
        <div className="page-header">
          <p>Pengaturan / Edit Profil</p>
          <h1>Edit Profil</h1>
        </div>
        <div className="content-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit}>
            
            <div className="form-group">
              <label>Nama</label>
              <input 
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                required 
              />
            </div>
            
            <div className="form-group">
              <label>Email</label>
              <input 
                type="email" 
                value={email} 
                readOnly 
                style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }} 
              />
            </div>
            
            <div className="form-group">
              <label>Role</label>
              <input 
                type="text" 
                value={role} 
                readOnly 
                style={{ textTransform: 'capitalize', backgroundColor: '#f0f0f0', cursor: 'not-allowed' }} 
              />
            </div>
            
            <div className="form-group">
              <label>Foto Profil</label>
              <div className="image-uploader-container">
                {/* Input file tersembunyi, selalu dirender */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileChange(e.target.files[0])}
                  style={{ display: 'none' }} // Tetap disembunyikan
                  accept="image/*"
                />
                
                {/* Area yang bisa diklik (preview atau area upload) */}
                <div
                  className={`drop-zone ${displayImage ? 'drop-zone--has-image' : ''}`}
                  style={{
                    backgroundImage: displayImage ? `url(${displayImage})` : 'none',
                    height: '150px',
                    width: '150px',
                    borderRadius: '50%', // Membuat area menjadi lingkaran
                    margin: '0 auto 15px auto', // Posisikan di tengah
                    cursor: 'pointer' 
                  }}
                  onClick={triggerFileSelect} // Panggil fungsi trigger saat area diklik
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleFileChange(e.dataTransfer.files[0]); }}
                >
                  {/* Tampilkan tombol hapus hanya jika ada PREVIEW BARU */}
                  {preview && (
                    <button 
                        type="button" 
                        className="drop-zone__remove-btn" 
                        onClick={handleRemovePreview} 
                        style={{ top: '5px', right: '5px' }} // Atur posisi tombol 'x'
                      >×</button>
                  )}
                  
                  {/* Tampilkan overlay atau prompt */}
                  {displayImage ? (
                    <div className="drop-zone__overlay" style={{borderRadius: '50%'}}><span>Ganti Foto</span></div>
                  ) : (
                    // Tampilan jika tidak ada gambar sama sekali
                    <div className="drop-zone__prompt">
                      <div className="drop-zone__prompt-icon" style={{fontSize: '32px', marginBottom: '5px'}}><FaUpload /></div>
                      <p style={{fontSize: '12px'}}>Pilih Foto</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f0f0f0' }}>
              <button 
                type="button" 
                onClick={() => setIsChangePasswordModalOpen(true)} 
                className="btn-secondary" 
                style={{ marginRight: '10px' }}
              >
                Ganti Password
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ChangePasswordModal 
        show={isChangePasswordModalOpen} 
        onClose={() => setIsChangePasswordModalOpen(false)} 
      />
    </>
  );
};

export default EditProfilePage;